import moment from 'moment';
import request from 'superagent/lib/client';
import apiUrlProvider from './ApiURL';
import UtapiMetrics from './UtapiMetrics';
import { MetricsApiError } from './metricsUtils';

/**
 * Split the given timeRange by intervalType
 * @param {Object} timerRange - Either [startDate] or [startDate, endDate].
 * @param {string} intervalType - 'days' or 'months
 *
 * @returns {Object} - An array of moment object representing dates
 */
function getRanges(timerRange, intervalType) {
    if (timerRange.length < 1) {
        return null;
    }

    const beginDate = moment(timerRange[0]);
    let endDate = moment();
    if (timerRange.length > 1) {
        endDate = moment(timerRange[1]);
    }

    if (!beginDate.isValid() || !endDate.isValid()) {
        return null;
    }

    const dates = [];
    let tmpDate = beginDate.clone();
    for (;;) {
        if (tmpDate >= endDate) {
            dates.push(tmpDate.format('YYYY-MM-DD'));
            return dates;
        }
        dates.push(tmpDate.format('YYYY-MM-DD'));
        tmpDate = tmpDate.clone().add(1, intervalType);
    }
}

export default class ScubaMetrics {
    constructor() {
        this.credentials = JSON.parse(localStorage.getItem('scality-credentials')) || '';
        this.utapi = new UtapiMetrics();
    }

    static _prepareDates(startDate, endDate) {
        const dates = [startDate.format('YYYY-MM-DD')];
        if (endDate) {
            dates.push(endDate.format('YYYY-MM-DD'));
        }
        return dates;
    }

    static _processMetrics(metrics, dates) {
        let downsampledMetrics = false;
        let downsampledMetricsMessage;

        const metricsResp = metrics.map((resourceMetrics) => {
            if (resourceMetrics.error) {
                // Ignore any scuba error (such as 403) to avoid disconnecting the user
                // for a non-critical error. Log if not a 400 InvalidURI, expected if bucket empty
                // To ease the first usages of scuba with identisee.
                if (resourceMetrics.error.code !== 400) {
                    // eslint-disable-next-line no-console
                    console.error('ScubaMetrics resourceMetrics error',
                        JSON.stringify(resourceMetrics, null, 2));
                }
                if (resourceMetrics.error.code /* === 400 || resourceMetrics.error.code === 416 */) {
                    if (resourceMetrics.error.code === 416) {
                        downsampledMetrics = true;
                        downsampledMetricsMessage = resourceMetrics.error.description;
                    }
                    return {
                        metricsClass: resourceMetrics.metricsClass,
                        resourceName: resourceMetrics.resourceName,
                        metrics: dates.map((date) => ({
                            date,
                            bytesTotal: 0,
                            objectsTotal: 0,
                        })),
                    };
                }
                throw new MetricsApiError(resourceMetrics.error.code, resourceMetrics.error.description);
            }

            const resourceDateMetrics = resourceMetrics.metrics.map((resourceDateMetrics) => {
                if (resourceDateMetrics.error) {
                    // Same, ignore scuba metrics error as above and log if not a 400 InvalidURI.
                    // Because we receive this error when the date has no metrics.
                    if (resourceDateMetrics.error.code !== 400) {
                        // eslint-disable-next-line no-console
                        console.error('ScubaMetrics resourceDateMetrics error',
                            JSON.stringify(resourceDateMetrics, null, 2));
                    }
                    if (resourceDateMetrics.error.code /* === 400 || resourceDateMetrics.error.code === 416 */) {
                        if (resourceDateMetrics.error.code === 416) {
                            downsampledMetrics = true;
                            downsampledMetricsMessage = resourceDateMetrics.error.description;
                        }

                        return {
                            date: resourceDateMetrics.date,
                            bytesTotal: 0,
                            objectsTotal: 0,
                        };
                    }
                    throw new MetricsApiError(resourceDateMetrics.error.code, resourceDateMetrics.error.description);
                }
                return resourceDateMetrics;
            });
            return {
                metricsClass: resourceMetrics.metricsClass,
                resourceName: resourceMetrics.resourceName,
                metrics: resourceDateMetrics,
            };
        });

        return { metrics: metricsResp, downsampledMetrics, downsampledMetricsMessage };
    }

    _doAPIRequest(url, body, cb) {
        return new Promise((resolve, reject) => {
            request
                .post(url)
                .set('x-access-token', this.credentials)
                .send(body)
                .end((err, response) => {
                    if (err) {
                        reject(err);
                    } else {
                        const resp = JSON.parse(response.text);
                        try {
                            const { metrics, downsampledMetrics, downsampledMetricsMessage } =
                                ScubaMetrics._processMetrics(resp.metrics, body.dates);
                            resolve(cb(metrics, downsampledMetrics, downsampledMetricsMessage));
                        } catch (error) {
                            reject(error);
                        }
                    }
                });
        });
    }

    _getSingleResourceMetrics(url, resourceNames, startDate, endDate) {
        const dates = ScubaMetrics._prepareDates(startDate, endDate);

        return this._doAPIRequest(
            url,
            { dates, resourceNames },
            (metrics, downsampledMetrics, downsampledMetricsMessage) => {
                const resourceMetrics = metrics[0];
                return {
                    metrics: {
                        storageUtilized: [
                            resourceMetrics.metrics[0].bytesTotal,
                            resourceMetrics.metrics[1].bytesTotal,
                        ],
                        numberOfObjects: [
                            resourceMetrics.metrics[0].objectsTotal,
                            resourceMetrics.metrics[1].objectsTotal,
                        ],
                    },
                    downsampledMetrics,
                    downsampledMetricsMessage,
                };
            }
        );
    }

    _getSingleResourceMetricsHistory(url, resourceNames, startDate, endDate, interval) {
        const timeRange = [startDate.format('YYYY-MM-DD')];
        if (endDate) {
            timeRange.push(endDate.format('YYYY-MM-DD'));
        }
        const dates = getRanges(timeRange, interval);

        return this._doAPIRequest(
            url,
            { dates, resourceNames },
            (metrics, downsampledMetrics, downsampledMetricsMessage) => {
                const metricsHistory = metrics[0].metrics.map((metrics) => ({
                    storageUtilized: metrics.bytesTotal,
                    // Pages do not use `numberOfObjects` from metrics history.
                }));

                return {
                    metrics: metricsHistory,
                    downsampledMetrics,
                    downsampledMetricsMessage,
                };
            }
        );
    }

    static async _mergeSingleResourceMetrics(promises) {
        const [
            { metrics: scubaMetrics, downsampledMetrics, downsampledMetricsMessage },
            { metrics: utapiMetrics },
        ] = await Promise.all(promises);

        return {
            metrics: {
                storageUtilized: scubaMetrics.storageUtilized,
                numberOfObjects: scubaMetrics.numberOfObjects,
                incomingBytes: utapiMetrics.incomingBytes,
                outgoingBytes: utapiMetrics.outgoingBytes,
                operations: utapiMetrics.operations,
            },
            downsampledMetrics,
            downsampledMetricsMessage,
        };
    }

    static async _mergeSingleResourceMetricsHistory(promises) {
        const [
            { metrics: scubaMetrics, downsampledMetrics, downsampledMetricsMessage },
            { metrics: utapiMetrics },
        ] = await Promise.all(promises);

        return {
            metrics: scubaMetrics.map((scubaMetrics, index) => (
                {
                    timeRange: utapiMetrics[index].timeRange,
                    storageUtilized: scubaMetrics.storageUtilized,
                    numberOfObjects: scubaMetrics.numberOfObjects,
                    incomingBytes: utapiMetrics[index].incomingBytes,
                    outgoingBytes: utapiMetrics[index].outgoingBytes,
                    operations: utapiMetrics[index].operations,
                }
            )),
            downsampledMetrics,
            downsampledMetricsMessage,
        };
    }

    /**
     * Extracts the latest valid (non-zero) bytesTotal value from resource metrics.
     * Iterates backward through the metrics array to find the most recent valid data.
     *
     * @param {Object} resourceMetrics - The metrics object for a single resource
     * @returns {number} - The latest valid bytesTotal value, or 0 if none found
     */
    static getLatestValidBytesTotal(resourceMetrics) {
        for (let i = resourceMetrics.metrics.length - 1; i >= 0; i--) {
            if (resourceMetrics.metrics[i].bytesTotal > 0) {
                return resourceMetrics.metrics[i].bytesTotal;
            }
        }
        return 0;
    }

    /**
    * Retrieve metrics for the given bucket.
    *
    * @param {Object} bucket - Name of the bucket to retrieve metrics for.
    * @param {Object} startDate - Moment object indicating the metrics start date.
    * @param {Object} endDate - Moment object indicating the metrics end date.
    *
    * @returns {Object} - An object with the following properties
    *                     - {number[]} storageUtilized: The amount of storage used (in bytes).
    *                     - {number[]} numberOfObjects: The number of objects stored.
    *                     - {number} incomingBytes: The amount of data received (in bytes).
    *                     - {number} outgoingBytes: The amount of data sent (in bytes).
    *                     - {Object} operations: The number of operations, for each operation type.
    */
    getBucketMetrics(bucket, startDate, endDate) {
        const promises = [];
        promises.push(this._getSingleResourceMetrics(
            `${apiUrlProvider.apiUrl()}/sur/bucket`,
            [bucket],
            startDate,
            endDate,
        ));
        promises.push(this.utapi.getBucketMetrics(bucket, startDate, endDate));

        return ScubaMetrics._mergeSingleResourceMetrics(promises);
    }

    /**
    * Retrieve metrics for the given account.
    *
    * @param {Object} account - Object containing information about an IAM account.
    * @param {string} account.accountId - The account ID.
    * @param {string} account.accountName - The account name.
    * @param {Object} startDate - Moment object indicating the metrics start date.
    * @param {Object} endDate - Moment object indicating the metrics end date.
    *
    * @returns {Object} - An object with the following properties
    *                     - {number[]} storageUtilized: The amount of storage used (in bytes).
    *                     - {number[]} numberOfObjects: The number of objects stored.
    *                     - {number} incomingBytes: The amount of data received (in bytes).
    *                     - {number} outgoingBytes: The amount of data sent (in bytes).
    *                     - {Object} operations: The number of operations, for each operation type.
    */
    getAccountMetrics(account, startDate, endDate) {
        const promises = [];
        promises.push(this._getSingleResourceMetrics(
            `${apiUrlProvider.apiUrl()}/sur/account`,
            [`${account.accountName}`],
            startDate,
            endDate,
        ));
        promises.push(this.utapi.getAccountMetrics(account, startDate, endDate));

        return ScubaMetrics._mergeSingleResourceMetrics(promises);
    }

    /**
    * Retrieve metrics for the s3 service.
    *
    * @param {Object} startDate - Moment object indicating the metrics start date.
    * @param {Object} endDate - Moment object indicating the metrics end date.
    *
    * @returns {Object} - An object with the following properties
    *                     - {number[]} storageUtilized: The amount of storage used (in bytes).
    *                     - {number[]} numberOfObjects: The number of objects stored.
    *                     - {number} incomingBytes: The amount of data received (in bytes).
    *                     - {number} outgoingBytes: The amount of data sent (in bytes).
    *                     - {Object} operations: The number of operations, for each operation type.
    */
    getSystemMetrics(startDate, endDate) {
        const promises = [];
        promises.push(this._getSingleResourceMetrics(
            `${apiUrlProvider.apiUrl()}/sur/service`,
            ['s3'],
            startDate,
            endDate,
        ));
        promises.push(this.utapi.getSystemMetrics(startDate, endDate));

        return ScubaMetrics._mergeSingleResourceMetrics(promises);
    }

    /**
    * Retrieve metrics for the given array of buckets.
    *
    * @param {string[]} buckets - Array of bucket names to retrieve metrics for.
    * @param {Object} startDate - Moment object indicating the metrics start date.
    * @param {Object} endDate - Moment object indicating the metrics end date.
    *
    * @returns {Array} - An array where each element is an object with the following properties:
    *                    - {string} bucketName: Bucket name.
    *                    - {number} storageUtilized: The amount of storage used (in bytes) at the end date.
    *                    - {number} incomingBytes: The amount of data received (in bytes).
    *                    - {number} outgoingBytes: The amount of data sent (in bytes).
    *                    Note that the `numberOfObjects` is not returned as it not used by the users of the API.
    */
    async getMultipleBucketsMetrics(buckets, startDate, endDate) {
        const dates = ScubaMetrics._prepareDates(startDate, endDate);

        const promises = [];
        promises.push(this._doAPIRequest(
            `${apiUrlProvider.apiUrl()}/sur/bucket`,
            { dates, resourceNames: buckets },
            (metrics, downsampledMetrics, downsampledMetricsMessage) => {
                const bucketMetrics = metrics.map((resourceMetrics) => ({
                    bucketName: resourceMetrics.resourceName,
                    storageUtilized: ScubaMetrics.getLatestValidBytesTotal(resourceMetrics),
                }));

                return {
                    metrics: bucketMetrics,
                    downsampledMetrics,
                    downsampledMetricsMessage,
                };
            }
        ));
        promises.push(this.utapi.getMultipleBucketsMetrics(buckets, startDate, endDate));

        const [
            { metrics: scubaMetrics, downsampledMetrics, downsampledMetricsMessage },
            { metrics: utapiMetrics },
        ] = await Promise.all(promises);

        const bucketMetrics = scubaMetrics.map((scubaMetric) => {
            for (const utapiMetric of utapiMetrics) {
                if (scubaMetric.bucketName === utapiMetric.bucketName) {
                    return {
                        bucketName: scubaMetric.bucketName,
                        storageUtilized: scubaMetric.storageUtilized,
                        // The Buckets page does not use `numberOfObjects` from bucket metrics.
                        incomingBytes: utapiMetric.incomingBytes,
                        outgoingBytes: utapiMetric.outgoingBytes,
                    };
                }
            }

            // We should not reach this point
            return undefined;
        });

        return {
            metrics: bucketMetrics,
            downsampledMetrics,
            downsampledMetricsMessage,
        };
    }

    /**
    * Retrieve metrics for the given array of accounts.
    *
    * @param {Object[]} accounts - Array where each element is an object with the following properties:
                                    - {string} account.accountId - Account ID.
                                    - {string} account.accountName - Account name.
    * @param {Object} startDate - Moment object indicating the metrics start date.
    * @param {Object} endDate - Moment object indicating the metrics end date.
    *
    * @returns {Array} - An array where each element is an object with the following properties:
    *                    - {string} accountId: Account ID.
    *                    - {number} storageUtilized: The amount of storage used (in bytes) at the end date.
    *                    - {number} incomingBytes: The amount of data received (in bytes).
    *                    - {number} outgoingBytes: The amount of data sent (in bytes).
    *                    Note that the `numberOfObjects` is not returned as it not used by the users of the API.
    */
    async getMultipleAccountsMetrics(accounts, startDate, endDate) {
        const dates = ScubaMetrics._prepareDates(startDate, endDate);

        const promises = [];
        promises.push(this._doAPIRequest(
            `${apiUrlProvider.apiUrl()}/sur/account`,
            { dates, resourceNames: accounts.map(item => item.accountName) },
            (metrics, downsampledMetrics, downsampledMetricsMessage) => {
                const accountMetrics = metrics.map((resourceMetrics) => {
                    for (const { accountName, accountId } of accounts) {
                        if (resourceMetrics.resourceName === accountName) {
                            return {
                                accountId,
                                storageUtilized: ScubaMetrics.getLatestValidBytesTotal(resourceMetrics),
                            };
                        }
                    }

                    // We should not reach this point
                    return undefined;
                });

                return {
                    metrics: accountMetrics,
                    downsampledMetrics,
                    downsampledMetricsMessage,
                };
            }
        ));

        promises.push(this.utapi.getMultipleAccountsMetrics(accounts, startDate, endDate));

        const [
            { metrics: scubaMetrics, downsampledMetrics, downsampledMetricsMessage },
            { metrics: utapiMetrics },
        ] = await Promise.all(promises);

        const accountMetrics = scubaMetrics.map((scubaMetric) => {
            for (const utapiMetric of utapiMetrics) {
                if (scubaMetric.accountId === utapiMetric.accountId) {
                    return {
                        accountId: scubaMetric.accountId,
                        storageUtilized: scubaMetric.storageUtilized,
                        // The Accounts page does not use `numberOfObjects` from account metrics
                        incomingBytes: utapiMetric.incomingBytes,
                        outgoingBytes: utapiMetric.outgoingBytes,
                    };
                }
            }

            // We should not reach this point
            return undefined;
        });

        return { metrics: accountMetrics, downsampledMetrics, downsampledMetricsMessage };
    }

    /**
    * Retrieve metrics history for the given bucket.
    *
    * @param {Object} bucket - Name of the bucket to retrieve metrics for.
    * @param {string} interval - Interval to be used for metrics history.
    * @param {Object} startDate - Moment object indicating the metrics start date.
    * @param {Object} endDate - Moment object indicating the metrics end date.
    *
    * @returns {Array} - An array where each element is an object with the following properties:
    *                    - {number} timeRange: The start date.
    *                    - {number} storageUtilized: The amount of storage used (in bytes) at the end date.
    *                    - {number} incomingBytes: The amount of data received (in bytes).
    *                    - {number} outgoingBytes: The amount of data sent (in bytes).
    *                    - {Object} operations: The number of operations, for each operation type.
    *                    Note that the `numberOfObjects` is not returned as it not used by the users of the API.
    */
    async getBucketMetricsHistory(bucket, interval, startDate, endDate) {
        const promises = [];
        promises.push(this._getSingleResourceMetricsHistory(
            `${apiUrlProvider.apiUrl()}/sur/bucket`,
            [bucket],
            startDate,
            endDate,
            interval,
        ));
        promises.push(this.utapi.getBucketMetricsHistory(bucket, interval, startDate, endDate));

        return ScubaMetrics._mergeSingleResourceMetricsHistory(promises);
    }

    /**
    * Retrieve metrics history for the given account.
    *
    * @param {Object} account - Object containing information about an IAM account.
    * @param {string} account.accountId - The account ID.
    * @param {string} account.accountName - The account name.
    * @param {string} interval - Interval to be used for metrics history.
    * @param {Object} startDate - Moment object indicating the metrics start date.
    * @param {Object} endDate - Moment object indicating the metrics end date.
    *
    * @returns {Array} - An array where each element is an object with the following properties:
    *                    - {number} timeRange: The start date.
    *                    - {number} storageUtilized: The amount of storage used (in bytes) at the end date.
    *                    - {number} incomingBytes: The amount of data received (in bytes).
    *                    - {number} outgoingBytes: The amount of data sent (in bytes).
    *                    - {Object} operations: The number of operations, for each operation type.
    *                    Note that the `numberOfObjects` is not returned as it not used by the users of the API.
    */
    async getAccountMetricsHistory(account, interval, startDate, endDate) {
        const promises = [];
        promises.push(this._getSingleResourceMetricsHistory(
            `${apiUrlProvider.apiUrl()}/sur/account`,
            [account.accountName],
            startDate,
            endDate,
            interval,
        ));
        promises.push(this.utapi.getAccountMetricsHistory(account, interval, startDate, endDate));

        return ScubaMetrics._mergeSingleResourceMetricsHistory(promises);
    }

    /**
    * Retrieve metrics history for the s3 service.
    *
    * @param {string} interval - Interval to be used for metrics history.
    * @param {Object} startDate - Moment object indicating the metrics start date.
    * @param {Object} endDate - Moment object indicating the metrics end date.
    *
    * @returns {Array} - An array where each element is an object with the following properties:
    *                    - {number} timeRange: The start date.
    *                    - {number} storageUtilized: The amount of storage used (in bytes) at the end date.
    *                    - {number} incomingBytes: The amount of data received (in bytes).
    *                    - {number} outgoingBytes: The amount of data sent (in bytes).
    *                    - {Object} operations: The number of operations, for each operation type.
    *                    Note that the `numberOfObjects` is not returned as it not used by the users of the API.
    */
    async getSystemMetricsHistory(interval, startDate, endDate) {
        const promises = [];
        promises.push(this._getSingleResourceMetricsHistory(
            `${apiUrlProvider.apiUrl()}/sur/service`,
            ['s3'],
            startDate,
            endDate,
            interval,
        ));
        promises.push(this.utapi.getSystemMetricsHistory(interval, startDate, endDate));

        return ScubaMetrics._mergeSingleResourceMetricsHistory(promises);
    }

    /**
    * Retrieve metrics history for the buckets with the most used storage.
    *
    * @param {string} interval - Interval to be used for metrics history.
    * @param {Object} startDate - Moment object indicating the metrics start date.
    * @param {Object} endDate - Moment object indicating the metrics end date.
    * @param {Array} topBuckets - Array of bucket names contains the buckets with the most storage utilization
    *
    * @returns {Array} - A 2-dimensional array.
    *                    The outer array contains metrics each period (e.g. day) in the given interval.
    *                    Each element inner array contains metrics for multiple buckets:
    *                    Each element of the inner array is an object with the following properties:
    *                    - {string} bucketName: Bucket name.
    *                    - {number} timeRange: The end date.
    *                    - {number} storageUtilized: The amount of storage used (in bytes) at the end date.
    */
    async getMultipleBucketsHistory(interval, startDate, endDate) {
        const dates = ScubaMetrics._prepareDates(startDate, endDate);

        const topBuckets = await new Promise((resolve, reject) => {
            request
                .post(`${apiUrlProvider.apiUrl()}/sur/topbuckets`)
                .set('x-access-token', this.credentials)
                .send({ dates })
                .end((err, response) => {
                    if (err) {
                        reject(err);
                    } else {
                        const resp = JSON.parse(response.text);
                        try {
                            resolve(resp);
                        } catch (error) {
                            reject(error);
                        }
                    }
                });
        })
        .then((buckets) => buckets);

        const dateRange = getRanges(dates, interval);

        const promises = [];
        promises.push(this._doAPIRequest(
            `${apiUrlProvider.apiUrl()}/sur/bucket`,
            { dates: dateRange, resourceNames: topBuckets },
            (metrics, downsampledMetrics, downsampledMetricsMessage) => (
                { metrics, downsampledMetrics, downsampledMetricsMessage }
            )));

        promises.push(this.utapi.getMultipleBucketsHistory(interval, startDate, endDate, topBuckets));

        const [
            { metrics: scubaMetrics, downsampledMetrics, downsampledMetricsMessage },
            { metrics: utapiMetrics },
        ] = await Promise.all(promises);

        let dateIdx = 0;
        const bucketsHistory = [];
        for (const utapiDateElem of utapiMetrics) {
            let bucketIdx = 0;
            const dateElem = [];
            for (const utapiBucketMetrics of utapiDateElem) {
                dateElem.push({
                    bucketName: utapiBucketMetrics.bucketName,
                    timeRange: utapiBucketMetrics.timeRange,
                    storageUtilized: scubaMetrics[bucketIdx].metrics[dateIdx].bytesTotal,
                });

                bucketIdx += 1;
            }
            bucketsHistory.push(dateElem);
            dateIdx += 1;
        }

        return {
            metrics: bucketsHistory,
            downsampledMetrics,
            downsampledMetricsMessage,
        };
    }
}



/** WEBPACK FOOTER **
 ** ./src/utils/ScubaMetrics.js
 **/